import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const PENDING_CART_ACTION_KEY = 'pending_cart_action';

const readPendingCartAction = () => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.sessionStorage.getItem(PENDING_CART_ACTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.productId) return null;
    return parsed;
  } catch {
    return null;
  }
};

const clearPendingCartAction = () => {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(PENDING_CART_ACTION_KEY);
  } catch {
    // ignore
  }
};

const resolveRedirectPath = (from) => {
  if (typeof from === 'string') return from;
  if (from?.pathname) {
    return `${from.pathname}${from.search || ''}${from.hash || ''}`;
  }
  return '/';
};

const Login = () => {
  const [formData, setFormData] = useState({
    loginId: '', // Can be email or phone
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPrompt, setShowForgotPrompt] = useState(false);
  const handledAuthenticatedRedirectRef = useRef(false);
  
  const { login, isAuthenticated, user } = useAuth();
  const { addToCart, fetchCartItems } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const shouldShowForgotPrompt = (message) => {
    const text = String(message || '').toLowerCase();
    return text.includes('invalid credentials') || text.includes('wrong password');
  };

  // If already logged in, redirect appropriately
  useEffect(() => {
    if (!isAuthenticated || handledAuthenticatedRedirectRef.current) {
      return;
    }

    handledAuthenticatedRedirectRef.current = true;

    const finalizeLogin = async () => {
      let targetPath = resolveRedirectPath(location.state?.from);
      const pendingCartAction = readPendingCartAction();

      if (pendingCartAction) {
        const result = await addToCart(pendingCartAction.productId, pendingCartAction.quantity || 1);
        if (result?.success) {
          await fetchCartItems();
          clearPendingCartAction();
          targetPath = pendingCartAction.redirectTo || targetPath;
        }
      }

      if ((user?.role === 'admin' || user?.role === 'employee') && targetPath.startsWith('/dashboard')) {
        navigate('/dashboard');
      } else if (targetPath !== '/login' && targetPath !== '/register') {
        navigate(targetPath || '/');
      } else {
        navigate('/');
      }
    };

    finalizeLogin();
  }, [addToCart, fetchCartItems, isAuthenticated, location.state, navigate, user]);

  const handleChange = (e) => {
    if (error) {
      setError('');
    }
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // E-commerce login - always use isDashboardLogin = false
      const result = await login(formData.loginId, formData.password, false);
      if (result.success) {
        setShowForgotPrompt(false);
      } else {
        setError(result.message);
        setShowForgotPrompt(shouldShowForgotPrompt(result.message));
      }
    } catch (error) {
      setError('Login failed. Please try again.');
      setShowForgotPrompt(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
              create a new account
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                id="loginId"
                name="loginId"
                type="text"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email or 10-digit Mobile Number"
                value={formData.loginId}
                onChange={handleChange}
              />
            </div>
            <div>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          {showForgotPrompt && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Wrong password?{' '}
              <Link to="/forgot-password" className="font-semibold text-blue-600 hover:text-blue-500">
                Forgot Password
              </Link>
              {' '}to continue.
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
